import React, { useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { fieldWorkList } from '../../ProData';
import { Sparkles, HeartHandshake, Compass } from 'lucide-react';
import { motion } from 'framer-motion';

export default function StudentFieldWork() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-5" style={{ background: '#f8fafc', minHeight: '85vh' }}>
      <Helmet>
        <title>Student Field Work &amp; Pastoral Ministry - Vidyajyoti College of Theology</title>
        <meta name="description" content="Apostolic involvement, pastoral reflection, and immersion with marginalized communities in Delhi by Vidyajyoti theology students." />
        <meta name="keywords" content="Student fieldwork, Pastoral reflection, Contextual ministry, Vidyajyoti students Delhi, Social action" />
        <link rel="canonical" href="/students/fieldwork" />
      </Helmet>

      <div className="container">
        {/* Header Hero */}
        <div className="text-center mx-auto pb-4" style={{ maxWidth: '800px' }}>
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(121, 3, 29, 0.08)', color: '#79031d', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(121, 3, 29, 0.2)' }}>
            <Sparkles size={14} /> Apostolic Praxis
          </div>
          <h1 className="display-6 fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
            Field Work &amp; Pastoral Reflection
          </h1>
          <p className="lead text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
            Active apostolic involvement throughout the academic year is an essential pillar of theological formation at Vidyajyoti. It aims to develop personal attitudes and skills, hone theological reflection on praxis, and cultivate guided self-evaluation.
          </p>
        </div>

        {/* Overview Box */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 mb-5" style={{ background: 'linear-gradient(135deg, #1a2b56 0%, #2a3f75 100%)', color: '#fff' }}>
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <h3 className="fw-bold mb-3 text-white">Theological Significance of Apostolic Engagement</h3>
              <p className="text-light opacity-90 mb-3" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                Fieldwork is not merely social assistance; it constitutes an indispensable hermeneutical key for contextual theology. By walking alongside urban poor, rickshaw pullers, slum children, refugees, and marginalized women, students encounter the living God in human struggles and bring those realities into the classroom for theological synthesis.
              </p>
              <div className="d-flex align-items-center gap-2 text-warning small fw-semibold">
                <Compass size={16} /> Monthly structured pastoral reflection sessions guided by faculty directors.
              </div>
            </div>
            <div className="col-lg-4 text-center">
              <div className="p-4 rounded-4" style={{ background: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                <HeartHandshake size={40} className="mb-2" style={{ color: '#ffd56b' }} />
                <h5 className="fw-bold text-white mb-1">8 Core Apostolates</h5>
                <p className="small text-light opacity-75 mb-0">Direct Community Presence</p>
              </div>
            </div>
          </div>
        </div>

        {/* Apostolates Grid */}
        <div className="text-center mb-4">
          <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: '#79031d', color: '#fff' }}>Active Sectors</span>
          <h3 className="fw-bold" style={{ color: '#1a2b56' }}>Current Areas of Pastoral Fieldwork</h3>
        </div>

        <div className="row g-4 justify-content-center">
          {fieldWorkList && fieldWorkList.map((item, index) => (
            <motion.div
              key={index}
              className="col-sm-6 col-md-4 col-lg-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
            >
              <div
                className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white position-relative d-flex flex-column"
                style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                }}
              >
                <div className="position-relative overflow-hidden" style={{ height: '200px', background: '#e2e8f0' }}>
                  <img
                    src={item.imageSrc}
                    className="w-100 h-100"
                    alt={item.field || 'Fieldwork apostolate'}
                    style={{ objectFit: 'cover' }}
                  />
                  <div
                    className="position-absolute bottom-0 start-0 end-0 p-2.5 text-white text-center"
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)' }}
                  >
                    <span className="badge px-2.5 py-1 rounded-pill" style={{ background: '#c69214', color: '#1a2b56', fontWeight: 600 }}>
                      Ministry {index + 1}
                    </span>
                  </div>
                </div>

                <div className="card-body p-3.5 text-center d-flex flex-column justify-content-center">
                  <h5 className="fw-bold mb-1" style={{ color: '#1a2b56', fontSize: '1.1rem' }}>
                    {item.field}
                  </h5>
                  <small className="text-muted">Apostolic Involvement</small>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
